import React, { useCallback, useContext, useReducer } from 'react';
import {
  Button, Dialog, DialogTitle, FormGroup,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import { buildingEntity, tenantEntity } from 'shared/entityDetails';
import { getTenantsInUnit, searchBuildingUnits } from 'shared/api/units';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { getTicket, updateTicketAssociation } from 'shared/api/support';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { RELOCATE_MODES } from 'shared/constants';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { ToggleSelect } from 'shared/components/abstract/interactables';
import { dataReducer } from 'shared/reducers';
import { CardSection } from 'shared/components/cardControls';
import { getTicketPath } from 'shared/routes/support';
import TicketMergeTable from './subcomponents/TicketMergeTable';

/**
 * @param {Object} props
 * @param {number} props.associationId
 * @param {Object} props.ticketAssociation
 * @param {Object} props.ticketFormState
 * @param {Function} props.setBuilding
 * @param {Function} props.setUnit
 * @param {Function} props.setTenant
 * @param {number} props.ticketId
 * @param {string} props.ticketTitle
 * @param {number} props.buildingId
 * @param {number} props.unitId
 * @param {number} props.tenantId
 * @param {Function} props.searchUnits
 * @param {Function} props.searchTenants
 * @param {Function} props.setEntries
 * @param {Function} props.setTicketAssociation
 */
export function TicketRelocateSearch(props) {
  const {
    associationId,
    ticketAssociation,
    ticketFormState,
    setBuilding,
    setUnit,
    setTenant,
    ticketId,
    ticketTitle,
    buildingId,
    unitId,
    tenantId,
    searchUnits,
    searchTenants,
    setEntries,
    setTicketAssociation,
  } = props;
  const {
    building,
    unit,
    tenant,
  } = ticketFormState;

  const { request } = useRequest();
  const notification = useNotification();
  const history = useHistory();
  const { user: { username } } = useAuth();
  const syncSideNav = useContext(SideNavContext);
  const [openEntryDialog, setOpenEntryDialog] = React.useState(false);
  const [show, setShow] = React.useState(RELOCATE_MODES.merge);

  const handleShow = (newShow) => {
    if (newShow !== null) {
      setShow(newShow);
    }
  };

  const handleOpenEntryDialog = () => {
    setOpenEntryDialog(true);
  };
  const handleCloseEntryDialog = () => setOpenEntryDialog(false);

  function destinationAssociationString() {
    let titleString = '';
    if (tenant) {
      titleString += `${tenant.first_name} ${tenant.last_name} | `;
    }
    if (unit) {
      titleString += `Unit ${unit.name} | `;
    }
    if (building) {
      titleString += `${building.name} `;
    }
    return titleString;
  }

  const sameAssociation = (
    (building?.name === ticketAssociation.building?.name)
    && (unit?.name === ticketAssociation.unit?.name)
    && (tenant?.first_name === ticketAssociation.tenant?.first_name)
    && (tenant?.last_name === ticketAssociation.tenant?.last_name)
  );

  const disableMove = (
    building === null || sameAssociation
  );

  const moveMessage = (disableMove ? 'Select a new association above to move this ticket' : (
    <div>
      Click the Move Ticket button to relocate this ticket to&nbsp;
      <b>{destinationAssociationString()}</b>
    </div>
  ));

  const mergeMessage = (
    <div>
      Choose a ticket for&nbsp;
      <b>{destinationAssociationString()}</b>
      from the table below to merge into this ticket by clicking the Merge icon on the row
    </div>
  );

  const submitTicketMove = async () => {
    // change the ticket association and then retrieve the updated ticket
    const moveTicketParams = {
      ticket_id: ticketId,
      building_id: buildingId,
      unit_id: unitId,
      tenant_id: tenantId,
      username,
    };

    const moveTicketResult = await request(
      updateTicketAssociation(associationId, moveTicketParams),
    );
    const newTicket = await request(
      getTicket(ticketId),
    );

    if (!moveTicketResult) {
      notification.pushError(`Failed to move Ticket ${ticketId}: ${ticketTitle}`);
      return;
    }

    setTicketAssociation(newTicket.ticket_association, newTicket.entries[0]);
    syncSideNav(getTicketPath(ticketId));
    handleCloseEntryDialog();
    history.push(getTicketPath(ticketId));
    notification.pushSuccess(`Moved Ticket ${ticketId}: ${ticketTitle} successfully`);
  };

  const ticketMoveDialog = (
    <>
      <Button
        id="new-entry-button"
        variant="contained"
        onClick={handleOpenEntryDialog}
        disabled={disableMove}
        sx={{ display: 'flex', ml: 'auto' }}
      >
        Move Ticket
      </Button>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openEntryDialog}
        onClose={handleCloseEntryDialog}
      >
        <DialogTitle>
          Move ticket to&nbsp;
          <b>{destinationAssociationString()}</b>
          ?
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
          <Button
            id="close-move-ticket-button"
            variant="outlined"
            onClick={() => handleCloseEntryDialog()}
          >
            Close
          </Button>
          <Button
            id="move-ticket-button"
            variant="contained"
            onClick={() => submitTicketMove()}
          >
            Confirm
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );

  const picker = (
    <Grid
      container
      size={12}
      rowSpacing={0}
      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
    >
      <Grid size={{ lg: 5, xs: 12 }}>
        <FormGroup>
          <AsyncSearchableSelect
            label="Building"
            baseSearch="?expand=units"
            defaultOptions={false}
            onChange={setBuilding}
            searchRequest={buildingEntity.searchRequest}
            selectedOption={building}
          />
        </FormGroup>
      </Grid>
      <Grid size={{ lg: 3, xs: 12 }}>
        <FormGroup>
          <AsyncSearchableSelect
            label="Unit"
            onChange={setUnit}
            searchRequest={searchUnits}
            disabled={buildingId === 0}
            selectedOption={unit}
          />
        </FormGroup>
      </Grid>
      <Grid size={{ lg: 4, xs: 12 }}>
        <FormGroup>
          <AsyncSearchableSelect
            baseSearch="include_archived=true&ob=it_firstname"
            getOptionLabel={tenantEntity.getOptionLabel}
            label="Tenant"
            onChange={setTenant}
            searchRequest={searchTenants}
            disabled={unitId === 0}
            selectedOption={tenant}
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <Grid container size={12}>
      <Grid size={12}>
        <form id="ticket-merge-form">
          {picker}
        </form>
        <br />
        <ToggleSelect
          setValue={handleShow}
          options={Object.keys(RELOCATE_MODES)
            .map((key) => ({
              name: key,
              value: key,
            }))}
          value={show}
        />
      </Grid>
      <br />
      <Grid size={12}>
        <div className="item-child-subtitle">
          {(show === RELOCATE_MODES.merge) ? mergeMessage : moveMessage}
        </div>
      </Grid>
      <Grid size={12}>
        {show === RELOCATE_MODES.move && ticketMoveDialog}
        {show === RELOCATE_MODES.merge
          && (
            <TicketMergeTable
              searchQuery={`?building_id=${buildingId}${unitId !== 0 && buildingId !== 0 ? `&unit_id=${unitId}` : ''}${tenantId !== 0 && unitId !== 0 && buildingId !== 0 ? `&tenant_id=${tenantId}` : ''}`}
              parentTicketId={ticketId}
              parentTicketTitle={ticketTitle}
              setEntries={setEntries}
            />
          )}
      </Grid>
      <br />
    </Grid>
  );
}

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'set':
      return {
        ...state,
        [payload.name]: payload.value,
      };

    default:
      return dataReducer(state, {
        action,
        payload,
      });
  }
}

/**
 * @param {Object} props
 * @param {Function} props.ticketDispatch
 * @param {Object} props.ticketState
 */
export default function TicketRelocate(props) {
  const {
    ticketState,
    ticketDispatch,
  } = props;
  const [relocateState, relocateDispatch] = useReducer(reducer, {
    building: ticketState.data.ticket_association.building,
    unit: ticketState.data.ticket_association.unit,
    tenant: ticketState.data.ticket_association.tenant,
  });

  const searchUnits = useCallback(
    (searchString) => searchBuildingUnits(searchString, '', (relocateState.building ? relocateState.building.id : 0)),
    [relocateState.building],
  );

  const searchTenants = useCallback(
    (searchString) => getTenantsInUnit(
      (relocateState.unit ? relocateState.unit.id : 0),
      searchString,
    ),
    [relocateState.unit],
  );

  const setEntries = useCallback((value) => {
    const entriesArray = ticketState.data.entries;
    const newEntriesArray = [value, ...entriesArray];
    ticketDispatch({
      action: 'set',
      payload: {
        ...ticketState.data,
        entries: newEntriesArray,
      },
    });
  }, [ticketDispatch, ticketState.data]);

  const setTicketAssociation = useCallback((newAssociation, newEntry) => {
    const entriesArray = ticketState.data.entries;
    const newEntriesArray = [newEntry, ...entriesArray];
    ticketDispatch({
      action: 'set',
      payload: {
        ...ticketState.data,
        ticket_association: newAssociation,
        entries: newEntriesArray,
      },
    });
  }, [ticketDispatch, ticketState.data]);

  return (
    <CardSection>
      <Box sx={{
        padding: '10px',
        backgroundColor: 'subtleBackground',
        borderRadius: '.25rem',
        marginBottom: '5px',
      }}
      >
        <Grid
          container
          size={12}
          rowSpacing={1}
          columnSpacing={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <div className="item-child-title">Search Tickets</div>
          <TicketRelocateSearch
            associationId={ticketState.data.ticket_association.id}
            ticketAssociation={ticketState.data.ticket_association}
            ticketFormState={relocateState}
            ticketId={ticketState.data.id}
            ticketTitle={ticketState.data.title}
            searchUnits={searchUnits}
            searchTenants={searchTenants}
            buildingId={(relocateState.building ? relocateState.building.id : 0)}
            unitId={(relocateState.unit ? relocateState.unit.id : 0)}
            tenantId={(relocateState.tenant ? relocateState.tenant.id : 0)}
            setBuilding={(newBuilding) => {
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'building',
                  value: newBuilding,
                },
              });
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'unit',
                  value: null,
                },
              });
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'tenant',
                  value: null,
                },
              });
            }}
            setUnit={(newUnit) => {
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'unit',
                  value: newUnit,
                },
              });
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'tenant',
                  value: null,
                },
              });
            }}
            setTenant={(newTenant) => {
              relocateDispatch({
                action: 'set',
                payload: {
                  name: 'tenant',
                  value: newTenant,
                },
              });
            }}
            setEntries={setEntries}
            setTicketAssociation={setTicketAssociation}
          />
        </Grid>
      </Box>
    </CardSection>
  );
}
